import React from 'react';
import {Feedback} from '../../../../data/models/Feedback/Feedback';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {formatDateTime} from '../../../../utils/notificationTimeTextConvertor';
import FeedbackPhonePreview from '../../components/FeedbackPhonePreview';
import {feedbackText} from '../../feedbackText';
import editorStyles from '../../../Microreels/List/components/MicroreelQuestionEditor.module.css';
import styles from './FeedbackDetailsUI.module.css';

interface Props {
  feedback?: Feedback;
  canEdit: boolean;
  isDeleting: boolean;
  onBack: () => void;
  onDelete: () => void;
}

const FeedbackDetailsUI = React.memo(
  ({feedback, canEdit, isDeleting, onBack, onDelete}: Props) => {
    if (!feedback) {
      return (
        <div className={styles.loading}>
          <LoadingSpinner size={48} />
        </div>
      );
    }

    const answers = [...feedback.answers].sort((x, y) => x.order - y.order);
    const trigger = feedback.numberOfFinishedLessonTrigger;
    const totalResponses = answers.reduce(
      (sum, answer) => sum + answer.totalAnswered,
      0,
    );
    const shareOf = (count: number) =>
      ((count / totalResponses) * 100).toFixed(1).replace(/\.0$/, '');

    return (
      <div className={styles.container}>
        <button className={styles.backLink} onClick={onBack}>
          ← {feedbackText.allFeedback}
        </button>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{feedback.title}</h1>
          {canEdit && (
            <button
              className={styles.deleteButton}
              onClick={onDelete}
              disabled={isDeleting}>
              {feedbackText.delete}
            </button>
          )}
        </div>
        {canEdit && <p className={styles.hint}>{feedbackText.noEditHint}</p>}

        <div className={styles.body}>
          <div className={editorStyles.phoneColumn}>
            <FeedbackPhonePreview
              title={feedback.title}
              question={feedback.question}
              answers={answers.map(answer => answer.answer)}
            />
            <p className={editorStyles.phoneCaption}>
              {feedbackText.showAfterCaption(trigger)}
            </p>
          </div>

          <div className={styles.info}>
            <div className={styles.field}>
              <p className={styles.label}>{feedbackText.questionLabel}</p>
              <p className={styles.value}>{feedback.question}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>{feedbackText.answersLabel}</p>
              <p className={styles.responses}>
                {feedbackText.responsesCount(totalResponses)}
              </p>
              <ol className={styles.answers}>
                {answers.map(answer => (
                  <li key={answer.id} className={styles.answer}>
                    <span className={styles.answerText}>{answer.answer}</span>
                    {totalResponses > 0 && (
                      <span className={styles.answerStat}>
                        {feedbackText.answerShare(
                          shareOf(answer.totalAnswered),
                          answer.totalAnswered,
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>{feedbackText.showAfterLabel}</p>
              <p className={styles.value}>
                {feedbackText.showAfterValue(trigger)}
              </p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>{feedbackText.createdLabel}</p>
              <p className={styles.value}>
                {formatDateTime(feedback.dateCreated)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default FeedbackDetailsUI;
